import React from 'react'
import { FileButton } from '@e3dc-react/shell/Elements/Buttons'
import { downloadNdaFile, fetchNdaFileLink } from '../api'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { NdaLanguage } from '../models/nda.model'
import { getCurrentLanguage } from 'src/Libs/languages/functions/default'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { makeStyles } from 'tss-react/mui'
import useIsWebviewMode from 'src/Hooks/use-is-webview-mode'

const useStyles = makeStyles()(() => ({
  ndaButton: {
    width: 'fit-content',
    height: 36,
    maxHeight: 36,
    flex: 1,
    minWidth: 0,
    marginRight: 0,
    maxWidth: 'fit-content',
  },
}))

interface NdaDownloadButtonProps {
  companyId?: number
  companyName?: string
  withMenu?: boolean
}

/**
 * Button to download a companies nda file
 * @param companyId - id of the company
 * @param companyName - name of the company
 * @param withMenu - if true, button click will render a menu to choose the language for the file. Else the user language will be chosen
 */
const NdaDownloadButton: React.FC<NdaDownloadButtonProps> = ({ companyId, companyName, withMenu = false }) => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()
  const { enqueueErrorStack } = useSnackbar()
  const { classes } = useStyles()
  const isWebview = useIsWebviewMode()

  const menuItems = [
    {
      label: t('common.languages.german'),
      onClick: () => handleDownload('de'),
    },
    {
      label: t('common.languages.english'),
      onClick: () => handleDownload('en'),
    },
  ]

  const handleDownload = async (language: NdaLanguage): Promise<void> => {
    try {
      const result = await fetchNdaFileLink(companyId!, authFetch, language)

      if (result.hasErrors || !result.value) {
        enqueueErrorStack(t('nda.download-button.error'))
      }

      if (result.value) {
        downloadNdaFile(result.value.ndaDownloadLink, companyName ?? '', isWebview)
      }
    } catch {
      enqueueErrorStack(t('nda.download-button.error'))
    }
  }

  return (
    <FileButton
      name={t('nda.download-button.title')}
      ext={'pdf'}
      hideExtension
      fullWidth={true}
      fileClassName={classes.ndaButton}
      onClickDownload={() => {
        handleDownload(getCurrentLanguage() === 'de' ? 'de' : 'en')
      }}
      apiEndpoint={process.env.REACT_APP_API_ENDPOINT ?? ''}
      menuItems={withMenu ? menuItems : undefined}
      openMenuOnButtonClick={withMenu}
      mimeType="application/pdf"
      isWebview={isWebview}
    />
  )
}

export default NdaDownloadButton
